/**
 * DEBUG ONLY — raw PKCE OAuth2 flow bypassing MSAL entirely.
 * Mirrors what the Chrome extension does via chrome.identity.launchWebAuthFlow.
 * Visit /auth-debug to use. Remove before shipping.
 */

import * as React from "react";
import { primer } from "../styles/primerDarkTheme";

const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID as string;
// Must match a registered redirect URI — use the existing redirect.html
const REDIRECT_URI = `${window.location.origin}/redirect.html`;

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBase64Url(byteCount: number): string {
    const arr = crypto.getRandomValues(new Uint8Array(byteCount));
    return btoa(String.fromCharCode(...arr))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

async function sha256Base64Url(plain: string): Promise<string> {
    const encoded = new TextEncoder().encode(plain);
    const digest = await crypto.subtle.digest("SHA-256", encoded);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// ── JWT decode ───────────────────────────────────────────────────────────────

function decodeJwt(token: string): Record<string, unknown> {
    try {
        const part = token.split(".")[1];
        return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
        return {};
    }
}

// ── Configs to test ──────────────────────────────────────────────────────────

interface TestConfig {
    label: string;
    authority: string;
    scopes: string[];
    extraParams?: Record<string, string>;
}

const CONFIGS: TestConfig[] = [
    {
        label: "/common + ARM scope + instance_aware",
        authority: "https://login.microsoftonline.com/common",
        scopes: ["https://management.azure.com/user_impersonation", "openid", "profile", "offline_access"],
        extraParams: { instance_aware: "true" },
    },
    {
        label: "/organizations + ARM scope + instance_aware",
        authority: "https://login.microsoftonline.com/organizations",
        scopes: ["https://management.azure.com/user_impersonation", "openid", "profile", "offline_access"],
        extraParams: { instance_aware: "true" },
    },
    {
        label: "/organizations + OIDC only + instance_aware",
        authority: "https://login.microsoftonline.com/organizations",
        scopes: ["openid", "profile", "offline_access"],
        extraParams: { instance_aware: "true" },
    },
    {
        label: "/common + OIDC only (baseline)",
        authority: "https://login.microsoftonline.com/common",
        scopes: ["openid", "profile", "offline_access"],
    },
];

// ── Component ─────────────────────────────────────────────────────────────────

const DebugAuth: React.FC = () => {
    const [result, setResult] = React.useState<Record<string, unknown> | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [configIdx, setConfigIdx] = React.useState<number | null>(null);
    const [exchanging, setExchanging] = React.useState(false);

    const exchangeCode = React.useCallback((code: string, verifier: string, cfg: TestConfig, idx: number) => {
        setExchanging(true);
        const tokenUrl = `${cfg.authority}/oauth2/v2.0/token`;
        const body = new URLSearchParams({
            grant_type: "authorization_code",
            client_id: CLIENT_ID,
            code,
            redirect_uri: REDIRECT_URI,
            code_verifier: verifier,
        });
        fetch(tokenUrl, { method: "POST", body, headers: { "Content-Type": "application/x-www-form-urlencoded" } })
            .then(r => r.json())
            .then((json: { access_token?: string; id_token?: string; error?: string; error_description?: string }) => {
                if (json.error) {
                    setError(`Token exchange failed: ${json.error}: ${json.error_description ?? ""}`);
                    return;
                }
                const token = json.access_token ?? json.id_token ?? "";
                const claims = decodeJwt(token);
                setResult({ config: cfg.label, ...claims });
                setConfigIdx(idx);
            })
            .catch(e => setError(String(e)))
            .finally(() => setExchanging(false));
    }, []);

    const startFlow = async (idx: number) => {
        const cfg = CONFIGS[idx];
        const verifier = randomBase64Url(32);
        const challenge = await sha256Base64Url(verifier);
        const state = randomBase64Url(16);

        const params = new URLSearchParams({
            client_id: CLIENT_ID,
            response_type: "code",
            redirect_uri: REDIRECT_URI,
            scope: cfg.scopes.join(" "),
            state,
            code_challenge: challenge,
            code_challenge_method: "S256",
            prompt: "select_account",
            ...cfg.extraParams,
        });

        const authUrl = `${cfg.authority}/oauth2/v2.0/authorize?${params.toString()}`;
        const popup = window.open(authUrl, "auth_debug_popup", "width=520,height=680,left=200,top=100");
        if (!popup) {
            setError("Popup was blocked — allow popups for this site and try again");
            return;
        }

        setExchanging(true);
        let handled = false;
        const interval = setInterval(() => {
            try {
                // readable once popup is back on our origin (redirect.html)
                const href = popup.location.href;
                if (href.includes("/redirect.html")) {
                    const search = popup.location.search;
                    handled = true;
                    clearInterval(interval);
                    popup.close();

                    const p = new URLSearchParams(search);
                    const code = p.get("code");
                    const err = p.get("error");
                    const errDesc = p.get("error_description");
                    const returnedState = p.get("state");

                    if (err) { setError(`${err}: ${errDesc ?? ""}`); setExchanging(false); return; }
                    if (!code || returnedState !== state) { setError("Missing code or state mismatch"); setExchanging(false); return; }
                    exchangeCode(code, verifier, cfg, idx);
                }
            } catch {
                // cross-origin while AAD is loading — keep polling
            }
            if (popup.closed) {
                clearInterval(interval);
                setExchanging(false);
                if (!handled) setError("Popup closed before completing sign-in");
            }
        }, 300);
    };

    const s: React.CSSProperties = {
        fontFamily: "monospace",
        background: primer.canvas,
        color: primer.fg,
        minHeight: "100vh",
        padding: 32,
    };

    return (
        <div style={s}>
            <h2 style={{ marginTop: 0 }}>Auth Debug (raw PKCE — no MSAL)</h2>
            <p style={{ color: primer.fgMuted, fontSize: 13 }}>
                Tests each authority/scope combination directly, mimicking the Chrome extension approach.
                Check the <code>tid</code> claim to see which tenant you land on.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 520 }}>
                {CONFIGS.map((cfg, i) => (
                    <button
                        key={i}
                        onClick={() => void startFlow(i)}
                        style={{
                            padding: "8px 14px",
                            borderRadius: 6,
                            border: `1px solid ${primer.border}`,
                            background: configIdx === i ? primer.blue : "transparent",
                            color: configIdx === i ? "#fff" : primer.fg,
                            cursor: "pointer",
                            textAlign: "left",
                            fontSize: 13,
                        }}
                    >
                        {cfg.label}
                    </button>
                ))}
            </div>

            {exchanging && (
                <p style={{ color: primer.fgMuted, marginTop: 24 }}>Exchanging code for token...</p>
            )}

            {error && (
                <div style={{ marginTop: 24, padding: 16, background: "#3d1a1a", borderRadius: 8, color: "#f87171", fontSize: 13 }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {result && (
                <div style={{ marginTop: 24 }}>
                    <h3 style={{ color: primer.blue }}>
                        tid = <span style={{ color: "#4ade80" }}>{String(result["tid"] ?? "not present")}</span>
                    </h3>
                    <pre style={{
                        background: "#0d1117",
                        border: `1px solid ${primer.border}`,
                        borderRadius: 8,
                        padding: 16,
                        fontSize: 12,
                        overflowX: "auto",
                        color: primer.fgMuted,
                    }}>
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default DebugAuth;
